/**
 * Validation utilities for FormBuilder
 * Provides validation functions (not regex patterns)
 */
import type { RegisterOptions } from 'react-hook-form'
import { FormFieldConfig } from '../types'

/**
 * Common validation functions
 * Returns true if valid, or error message string if invalid
 */
export const validators = {
  /**
   * Email validation
   */
  isEmail: (value: string): boolean | string => {
    if (!value) return true
    const isValid = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
    return isValid || 'Please enter a valid email address'
  },

  /**
   * Phone number validation (US format)
   */
  isPhone: (value: string): boolean | string => {
    if (!value) return true
    const cleaned = value.replace(/\D/g, '')
    return (
      cleaned.length === 10 ||
      cleaned.length === 11 ||
      'Please enter a valid phone number'
    )
  },

  /**
   * URL validation
   */
  isUrl: (value: string): boolean | string => {
    if (!value) return true
    try {
      new URL(value)
      return true
    } catch {
      return 'Please enter a valid URL'
    }
  },

  /**
   * Numeric validation
   */
  isNumeric: (value: string): boolean | string => {
    if (!value) return true
    return !isNaN(Number(value)) || 'Please enter a valid number'
  },

  /**
   * Integer validation
   */
  isInteger: (value: string): boolean | string => {
    if (!value) return true
    return Number.isInteger(Number(value)) || 'Please enter a whole number'
  },

  /**
   * Match another field
   */
  matchField: (fieldName: string, customMessage?: string) => {
    return (value: any, formValues: Record<string, any>): boolean | string => {
      return (
        value === formValues[fieldName] ||
        customMessage ||
        `Must match ${fieldName}`
      )
    }
  },

  /**
   * Different from another field
   */
  notMatchField: (fieldName: string, customMessage?: string) => {
    return (value: any, formValues: Record<string, any>): boolean | string => {
      return (
        value !== formValues[fieldName] ||
        customMessage ||
        `Must be different from ${fieldName}`
      )
    }
  },

  /**
   * Not empty (for trimmed strings)
   */
  notEmpty: (value: string): boolean | string => {
    if (!value) return true
    return value.trim().length > 0 || 'Cannot be empty or whitespace only'
  },

  /**
   * Contains no spaces
   */
  noSpaces: (value: string): boolean | string => {
    if (!value) return true
    return !/\s/.test(value) || 'Spaces are not allowed'
  }
}

/**
 * Quick validation rule builders
 */
export const quickRules = {
  /**
   * Email validation
   */
  email: (message?: string) => ({
    validate: (value: string) =>
      validators.isEmail(value) === true ||
      message ||
      'Please enter a valid email address'
  }),

  /**
   * Phone validation
   */
  phone: (message?: string) => ({
    validate: (value: string) =>
      validators.isPhone(value) === true ||
      message ||
      'Please enter a valid phone number'
  }),

  /**
   * Min value
   */
  min: (minValue: number, message?: string) => ({
    min: {
      value: minValue,
      message: message || `Must be at least ${minValue}`
    }
  }),

  /**
   * Max value
   */
  max: (maxValue: number, message?: string) => ({
    max: {
      value: maxValue,
      message: message || `Must be at most ${maxValue}`
    }
  }),

  /**
   * Min length
   */
  minLength: (length: number, message?: string) => ({
    minLength: {
      value: length,
      message: message || `Must be at least ${length} characters`
    }
  }),

  /**
   * Max length
   */
  maxLength: (length: number, message?: string) => ({
    maxLength: {
      value: length,
      message: message || `Must be at most ${length} characters`
    }
  }),

  /**
   * Range (min and max)
   */
  range: (min: number, max: number, message?: string) => ({
    min: {
      value: min,
      message: message || `Must be between ${min} and ${max}`
    },
    max: {
      value: max,
      message: message || `Must be between ${min} and ${max}`
    }
  })
}

/**
 * Merge basic required rule with custom validation rules
 * @param config - Field configuration
 * @returns Merged validation rules for react-hook-form
 */
export const buildValidationRules = (
  config: FormFieldConfig
): RegisterOptions => {
  const { required, label, rules } = config

  const validationRules: RegisterOptions = {
    required: required ? `${label} is required` : false
  }

  // Merge custom rules if provided
  if (rules) {
    Object.assign(validationRules, rules)
  }

  return validationRules
}
